import type { APIDesignerStore } from '.';
import type { OpenAPIV3 } from 'openapi-types';

export const SCHEMA_TYPES = [
  'array',
  'boolean',
  'integer',
  'number',
  'object',
  'string',
] as const satisfies OpenAPIV3.SchemaObject['type'][];

export type SchemaType = (typeof SCHEMA_TYPES)[number];

export type SchemaFormat =
  | 'binary'
  | 'byte'
  | 'date-time'
  | 'date'
  | 'double'
  | 'float'
  | 'int32'
  | 'int64'
  | 'json'
  | 'password';

interface BasicType {
  icon: string;
  type: SchemaType;
}

interface PrimitiveType extends BasicType {
  format: SchemaFormat;
}

interface NumberType extends PrimitiveType {
  type: 'integer' | 'number';
}

interface StringType extends PrimitiveType {
  type: 'string';
}

export const basicTypes = [
  {
    icon: 'string',
    type: 'string',
  },
  {
    icon: 'integer',
    type: 'number',
  },
  {
    icon: 'integer',
    type: 'integer',
  },
  {
    icon: 'binary',
    type: 'boolean',
  },
] as const satisfies BasicType[];

export const numberTypes = [
  {
    icon: 'life-buoy',
    type: 'number',
    format: 'float',
  },
  {
    icon: 'double',
    type: 'number',
    format: 'double',
  },
  {
    icon: 'integer',
    type: 'integer',
    format: 'int32',
  },
  {
    icon: 'integer',
    type: 'integer',
    format: 'int64',
  },
] as const satisfies NumberType[];

export const stringTypes = [
  {
    icon: 'calendar',
    type: 'string',
    format: 'date',
  },
  {
    icon: 'clock',
    type: 'string',
    format: 'date-time',
  },
  {
    icon: 'lock',
    type: 'string',
    format: 'password',
  },
  {
    icon: 'string',
    type: 'string',
    format: 'byte',
  },
  {
    icon: 'binary',
    type: 'string',
    format: 'binary',
  },
  {
    icon: 'json',
    type: 'string',
    format: 'json',
  },
] as const satisfies StringType[];

export type EnumValue = NonNullable<OpenAPIV3.SchemaObject['enum']>[number];

export function isEnumTypeSupported(type: OpenAPIV3.SchemaObject['type']): boolean {
  return basicTypes.some(basicType => basicType.type === type);
}

export type SupportedTypes = boolean | number | string;
// Type conversions
function convertBooleanToType(value: boolean, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'integer':
    case 'number':
    case 'boolean':
    default:
      return value;
  }
}

function convertNumberToType(value: number, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'string':
      return value.toString();
    case 'boolean':
      if (value === 1) return true;
      else if (value === 0) return false;
      return value;
    case 'integer':
    case 'number':
    default:
      return value;
  }
}

function convertStringToType(value: string, targetType: SchemaType): SupportedTypes {
  switch (targetType) {
    case 'boolean':
      if (value.toLowerCase() === 'true') return true;
      else if (value.toLowerCase() === 'false') return false;
      return value;
    case 'integer': {
      const intNum = Number(value);
      return Number.isNaN(intNum) || !Number.isInteger(intNum) ? value : intNum;
    }
    case 'number': {
      const num = Number(value);
      return Number.isNaN(num) ? value : num;
    }
    case 'string':
    default:
      return value;
  }
}

// Converts a type to a different type if possible
// If not possible, returns the original value with the original type
export function convertToType(value: EnumValue, type: SchemaType | undefined): SupportedTypes {
  // If the value is already the correct type or the type is not supported, return the original value
  const valueType = typeof value;
  if (valueType === type || !type || !isEnumTypeSupported(type)) {
    return value;
  }

  switch (typeof value) {
    case 'boolean':
      return convertBooleanToType(value, type);
    case 'number':
      return convertNumberToType(value, type);
    case 'string':
      return convertStringToType(value, type);
    default:
      return value;
  }
}

/**
 * Validates the API Designer state and returns the validated values.
 * This function can be used to just validate the state or to validate the state and return the validated values.
 * Throws an error if any required properties are missing.
 */
export const getValidatedAPIDesignerState = (state: APIDesignerStore) => {
  if (!state.apiObject) throw new Error('apiObject is required');
  if (!state.apiObject.schema) throw new Error('schema is required');
  if (!state.apiObject.schema.paths) throw new Error('paths is required');
  if (!state.apiObject.path) throw new Error('path is required');
  if (!state.apiObject.method) throw new Error('method is required');

  const currentPath = state.apiObject.path;
  const currentMethod = state.apiObject.method;

  if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
    throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
  }

  return {
    apiObject: state.apiObject,
    schema: state.apiObject.schema,
    paths: state.apiObject.schema.paths,
    path: currentPath,
    method: currentMethod,
    operation: state.apiObject.schema.paths[currentPath][currentMethod] as OpenAPIV3.OperationObject,
  };
};
